// ============================================================
// Pay Rocky - Admin Dashboard
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  TrendingUp,
  Wallet,
  CircleDollarSign,
  Landmark,
  LogOut,
  CheckCircle,
  ShieldCheck,
  History,
  XCircle,
  Bell,
  Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { useRates } from '../../context/RatesContext';
import { useWallets } from '../../context/WalletsContext';
import { useBankSettings } from '../../context/BankSettingsContext';
import { Badge, Card, MetricCard, Button, Divider, LoadingCard, Skeleton } from '../../components/ui/Primitives';
import CryptoLogo from '../../components/CryptoLogo';
import { API_BASE_URL, CRYPTO_LIST } from '../../data/constants';
import { apiRequest } from '../../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../../utils/idempotency';
import { disableAdminPushNotifications, enableAdminPushNotifications, getPushSupportState } from '../../utils/push';
import { formatNGN, formatCrypto, formatDate, formatDateShort, capitalize, maskDocumentNumber, truncateAddress } from '../../utils/helpers';

const NAV = [
  { id: 'analytics', label: 'Analytics', icon: LayoutDashboard },
  { id: 'orders', label: 'Orders', icon: ShoppingCart },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'rates', label: 'Rates', icon: TrendingUp },
  { id: 'rate-history', label: 'Rate History', icon: History },
  { id: 'health', label: 'System Health', icon: ShieldCheck },
  { id: 'banking', label: 'Banking', icon: Landmark },
  { id: 'wallets', label: 'Wallets', icon: Wallet },
  { id: 'treasury', label: 'Treasury', icon: CircleDollarSign },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');
const ADMIN_PANEL_REQUEST_TIMEOUT_MS = 10000;
const STABLE_RATE_INPUT_IDS = new Set(['usdt', 'usdc']);
const WORKFLOW_STAGE_OPTIONS = ['AWAITING_PAYMENT', 'AWAITING_CRYPTO', 'UNDER_REVIEW', 'CRYPTO_SEND_PENDING', 'NAIRA_PAYOUT_PENDING', 'FLAGGED', 'DISPUTED', 'RESOLVED', 'COMPLETED', 'CANCELLED'];
const DISPUTE_STATUS_OPTIONS = ['none', 'disputed', 'resolved'];
const CHECKLIST_FIELDS = [
  { key: 'payment_confirmed', label: 'Payment confirmed' },
  { key: 'proof_reviewed', label: 'Proof reviewed' },
  { key: 'wallet_verified', label: 'Wallet verified' },
  { key: 'payout_approved', label: 'Payout approved' },
  { key: 'payout_sent', label: 'Payout sent' },
];

function getAdminRateInputValue(cryptoId, storedRate, usdPrice) {
  if (!storedRate) return 0;
  if (STABLE_RATE_INPUT_IDS.has(cryptoId)) return storedRate;
  if (!usdPrice) return 0;
  return storedRate / usdPrice;
}

function toEditableRateValue(value) {
  if (!Number.isFinite(Number(value)) || Number(value) <= 0) return '';
  return String(Number(value.toFixed ? value.toFixed(2) : value));
}

function toEditableFeeValue(value) {
  if (!Number.isFinite(Number(value)) || Number(value) < 0) return '';
  return String(Number(value));
}

function toEditableNetworkFees(networkFees = {}, networks = []) {
  return networks.reduce((acc, network) => {
    acc[network] = toEditableFeeValue(networkFees?.[network] ?? 0);
    return acc;
  }, {});
}

function getStoredRateFromAdminInput(cryptoId, adminValue, usdPrice) {
  const numericValue = parseFloat(adminValue) || 0;
  if (STABLE_RATE_INPUT_IDS.has(cryptoId)) return numericValue;
  if (!usdPrice) return 0;
  return numericValue * usdPrice;
}

function resolveUploadedAssetUrl(fileUrl) {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl)) return encodeURI(fileUrl);
  const normalizedPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  return `${UPLOADS_BASE_URL}${encodeURI(normalizedPath)}`;
}

function copyToClipboard(value, successMessage = 'Copied!') {
  if (!value) {
    toast.error('Nothing to copy.');
    return;
  }
  navigator.clipboard.writeText(String(value));
  toast.success(successMessage);
}

function normalizeHomepageBanner(source = {}) {
  return {
    image_url: source.image_url || '',
    whatsapp_number: String(source.whatsapp_number || '').trim(),
    whatsapp_message: source.whatsapp_message || '',
    updated_at: source.updated_at || '',
  };
}

function normalizeNotificationSettings(source = {}) {
  return {
    email_enabled: source.email_enabled !== false,
    in_app_enabled: source.in_app_enabled !== false,
    push_enabled: source.push_enabled !== false,
    recipient_emails: Array.isArray(source.recipient_emails) ? source.recipient_emails : [],
    push_subscription_count: Number(source.push_subscription_count || 0),
    event_preferences: {
      new_buy_order: source.event_preferences?.new_buy_order !== false,
      new_sell_order: source.event_preferences?.new_sell_order !== false,
      proof_uploaded: source.event_preferences?.proof_uploaded !== false,
      kyc_submitted: source.event_preferences?.kyc_submitted !== false,
    },
  };
}

function normalizeSearchText(value) {
  return String(value || '').trim().toLowerCase();
}

function isWithinDateRange(value, fromDate, toDate) {
  if (!value) return false;
  const subjectDate = new Date(value);
  if (Number.isNaN(subjectDate.getTime())) return false;

  if (fromDate) {
    const start = new Date(fromDate);
    start.setHours(0, 0, 0, 0);
    if (subjectDate < start) return false;
  }

  if (toDate) {
    const end = new Date(toDate);
    end.setHours(23, 59, 59, 999);
    if (subjectDate > end) return false;
  }

  return true;
}

function formatWorkflowStage(stage) {
  return String(stage || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ') || '-';
}

function getEditableWorkflowStageOptions(orderType) {
  return String(orderType || '').trim().toLowerCase() === 'sell'
    ? ['AWAITING_CRYPTO', 'UNDER_REVIEW', 'NAIRA_PAYOUT_PENDING']
    : ['AWAITING_PAYMENT', 'UNDER_REVIEW', 'CRYPTO_SEND_PENDING'];
}

function getEditableWorkflowStageValue(order) {
  const options = getEditableWorkflowStageOptions(order?.type);
  if (options.includes(order?.workflow_stage)) return order.workflow_stage;
  if (String(order?.status || '').toUpperCase() === 'PENDING') {
    return String(order?.type || '').trim().toLowerCase() === 'sell' ? 'AWAITING_CRYPTO' : 'AWAITING_PAYMENT';
  }
  return 'UNDER_REVIEW';
}

function formatDisputeStatus(status) {
  return String(status || 'none')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ');
}

function getChecklistProgress(checklist = {}) {
  const completed = CHECKLIST_FIELDS.filter(({ key }) => Boolean(checklist?.[key])).length;
  return `${completed}/${CHECKLIST_FIELDS.length}`;
}

function normalizeWalletDraft(config = {}) {
  return {
    address: String(config?.address || ''),
    isEnabled: config?.isEnabled !== false,
    disabledReason: String(config?.disabledReason || ''),
  };
}

function normalizeTreasuryState(source = {}) {
  const assetLiquidity = source?.asset_liquidity && typeof source.asset_liquidity === 'object'
    ? Object.entries(source.asset_liquidity).reduce((acc, [cryptoId, networks]) => {
        acc[cryptoId] = Object.entries(networks || {}).reduce((networkAcc, [network, amount]) => {
          networkAcc[network] = String(Number(amount || 0));
          return networkAcc;
        }, {});
        return acc;
      }, {})
    : {};

  return {
    naira_available: String(Number(source?.naira_available || 0)),
    asset_liquidity: assetLiquidity,
    updated_at: source?.updated_at || '',
  };
}

export default function AdminDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('analytics');
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState(() => normalizeNotificationSettings({}));
  const [unreadCount, setUnreadCount] = useState(0);
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    dailyVolume: 0,
    integrity: null,
    treasury: null,
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function hydrateDashboard() {
      try {
        const [notificationsRes, settingsRes, analyticsRes] = await Promise.all([
          apiRequest('/admin/notifications', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS }),
          apiRequest('/admin/notification-settings', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS }),
          apiRequest('/admin/analytics', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS }),
        ]);
        if (cancelled) return;
        setNotifications(notificationsRes.notifications || []);
        setUnreadCount(Number(notificationsRes.unreadCount || 0));
        setNotificationSettings(normalizeNotificationSettings(settingsRes.settings));
        setAnalytics({
          totalUsers: Number(analyticsRes.totalUsers || 0),
          totalOrders: Number(analyticsRes.totalOrders || 0),
          pendingOrders: Number(analyticsRes.pendingOrders || 0),
          completedOrders: Number(analyticsRes.completedOrders || 0),
          dailyVolume: Number(analyticsRes.dailyVolume || 0),
          integrity: analyticsRes.integrity || null,
          treasury: analyticsRes.treasury || null,
        });
      } catch (_) {
      } finally {
        if (!cancelled) {
          setAnalyticsLoading(false);
        }
      }
    }

    hydrateDashboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const panelLabel = NAV.find((item) => item.id === active)?.label || 'Dashboard';

  return (
    <div className="dashboard-shell dashboard-shell-admin">
      <aside className="dashboard-sidebar admin-dashboard-sidebar">
        <Link to="/" className="dashboard-sidebar-brand">
          <div className="dashboard-sidebar-brand-mark dashboard-sidebar-brand-mark-admin">A</div>
          <div>
            <div className="dashboard-sidebar-brand-text">Pay<span style={{ color: 'var(--clr-accent)' }}>Rocky</span></div>
            <div className="dashboard-sidebar-brand-sub">Admin Command Center</div>
          </div>
        </Link>

        <Divider style={{ margin: '0 0 1rem' }} />

        <div className="dashboard-sidebar-profile">
          <div className="dashboard-sidebar-avatar dashboard-sidebar-avatar-admin">
            {(currentUser?.name || 'Admin').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="dashboard-sidebar-profile-name">{currentUser?.name || 'PayRocky Admin'}</div>
            <div className="dashboard-sidebar-profile-meta">{currentUser?.email || 'Administrator'}</div>
          </div>
        </div>

        <div className="dashboard-sidebar-section-label">Workspace</div>

        <nav className="dashboard-nav" style={{ flex: 1 }}>
          {NAV.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActive(id)} className={`dashboard-nav-button ${active === id ? 'is-active is-admin' : ''}`}>
              <Icon size={15} />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                {label}
                {id === 'notifications' && unreadCount > 0 ? (
                  <span style={{ minWidth: '18px', height: '18px', padding: '0 6px', borderRadius: '999px', background: 'var(--clr-danger)', color: '#fff', fontSize: '10px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </nav>

        <Card className="dashboard-sidebar-card dashboard-sidebar-card-admin">
          <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Live focus</div>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>{panelLabel}</div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>
            Move between operations, rates, audits, and configuration from one controlled workspace.
          </div>
        </Card>

        <div style={{ paddingTop: '0.9rem' }}>
          <button onClick={() => { logout(); navigate('/'); }} className="dashboard-logout-button">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <main className="dashboard-main admin-dashboard-main">
        <div className="dashboard-topbar">
          <div>
            <div className="dashboard-eyebrow">Admin Workspace</div>
            <h1 className="dashboard-page-title">{panelLabel}</h1>
          </div>
          <div className="dashboard-pill-row">
            <span className="dashboard-pill dashboard-pill-admin">{unreadCount > 0 ? `${unreadCount} unread alerts` : 'Alerts synced'}</span>
            <span className="dashboard-pill">{analytics.integrity?.ok ? 'System healthy' : 'System needs checks'}</span>
          </div>
        </div>

        <div className="admin-dashboard-mobile-shell dashboard-mobile-shell">
          <Card style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <Link to="/" className="dashboard-sidebar-brand" style={{ marginBottom: 0 }}>
                <div className="dashboard-sidebar-brand-mark dashboard-sidebar-brand-mark-admin" style={{ width: '28px', height: '28px' }}>A</div>
                <span className="dashboard-sidebar-brand-text" style={{ color: 'var(--clr-text)' }}>Pay<span style={{ color: 'var(--clr-accent)' }}>Rocky</span></span>
              </Link>
              <span className="dashboard-pill dashboard-pill-admin">ADMIN</span>
            </div>

            <div className="admin-dashboard-mobile-nav dashboard-mobile-nav-grid">
              {NAV.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setActive(id)} className={`dashboard-mobile-nav-button ${active === id ? 'is-active is-admin' : ''}`}>
                  <Icon size={16} />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {label}
                    {id === 'notifications' && unreadCount > 0 ? (
                      <span style={{ minWidth: '16px', height: '16px', padding: '0 4px', borderRadius: '999px', background: 'var(--clr-danger)', color: '#fff', fontSize: '9px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>

            <button onClick={() => { logout(); navigate('/'); }} className="dashboard-logout-button" style={{ width: '100%', justifyContent: 'center' }}>
              <LogOut size={13} /> Sign out
            </button>
          </Card>
        </div>

        {active === 'analytics' && <Analytics analytics={analytics} analyticsLoading={analyticsLoading} />}
        {active === 'orders' && <Orders />}
        {active === 'users' && <UsersPanel />}
        {active === 'rates' && <RatesPanel />}
        {active === 'rate-history' && <RateHistoryPanel />}
        {active === 'health' && <SystemHealthPanel initialIntegrity={analytics.integrity} />}
        {active === 'banking' && <BankingPanel />}
        {active === 'wallets' && <WalletsPanel />}
        {active === 'treasury' && <TreasuryPanel initialTreasurySummary={analytics.treasury} />}
        {active === 'notifications' && (
          <NotificationsPanel
            notifications={notifications}
            setNotifications={setNotifications}
            unreadCount={unreadCount}
            setUnreadCount={setUnreadCount}
            notificationSettings={notificationSettings}
            setNotificationSettings={setNotificationSettings}
          />
        )}
      </main>

      <style>{`
        @media (max-width: 960px) {
          .admin-dashboard-sidebar {
            display: none !important;
          }

          .admin-dashboard-mobile-shell {
            display: block !important;
          }
        }

        @media (max-width: 560px) {
          .admin-dashboard-mobile-nav {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
}

function Analytics({ analytics, analyticsLoading }) {
  const { orders, getStats, loading: ordersLoading } = useOrders();
  const stats = getStats();
  const showLoading = (ordersLoading && (!Array.isArray(orders) || orders.length === 0)) || analyticsLoading;
  const integrity = analytics?.integrity || null;
  const treasury = analytics?.treasury || null;

  return (
    <div className="fade-up">
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 800, marginBottom: '1.75rem' }}>Admin Overview</h1>

      <div className="admin-metric-grid dashboard-metric-grid dashboard-metric-grid-admin" style={{ marginBottom: '2rem' }}>
        {showLoading ? (
          Array.from({ length: 7 }).map((_, index) => (
            <Card key={`admin-metric-skeleton-${index}`} style={{ padding: '1rem' }}>
              <Skeleton width="40%" height="11px" style={{ marginBottom: '0.9rem' }} />
              <Skeleton width={index >= 5 ? '72%' : '50%'} height="26px" radius="12px" />
            </Card>
          ))
        ) : (
          <>
            <MetricCard label="Total users" value={analytics.totalUsers} />
            <MetricCard label="All orders" value={Number.isFinite(analytics.totalOrders) ? analytics.totalOrders : stats.total} />
            <MetricCard label="Pending" value={Number.isFinite(analytics.pendingOrders) ? analytics.pendingOrders : stats.pending} />
            <MetricCard label="Completed" value={Number.isFinite(analytics.completedOrders) ? analytics.completedOrders : stats.completed} accent />
            <MetricCard label="Daily vol" value={analytics.dailyVolume > 0 ? formatNGN(analytics.dailyVolume) : 'N0'} />
            <MetricCard label="Total volume" value={stats.completedVolume > 0 ? formatNGN(stats.completedVolume) : 'N0'} />
            <MetricCard label="Needs action" value={stats.pending + stats.processing} />
            <MetricCard label="Naira liquidity" value={treasury ? formatNGN(treasury.naira_available || 0) : 'N0'} />
            <MetricCard label="Pending payouts" value={treasury ? formatNGN(treasury.pending_sell_naira_outflow || 0) : 'N0'} />
          </>
        )}
      </div>

      {!showLoading && integrity && !integrity.ok ? (
        <Card style={{ marginBottom: '1rem', border: '1px solid rgba(255, 171, 0, 0.28)', background: 'rgba(255, 171, 0, 0.08)' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '6px' }}>Platform checks need attention</div>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7, marginBottom: '10px' }}>
            {integrity.issueCount} issue{integrity.issueCount === 1 ? '' : 's'} detected across rates, wallets, or business configuration.
          </div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {(Array.isArray(integrity.issues) ? integrity.issues.slice(0, 5) : []).map((issue) => (
              <div key={`${issue.code}-${issue.target}`} style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border)', fontSize: '12px', color: 'var(--clr-text-2)' }}>
                <strong style={{ color: 'var(--clr-text)' }}>{issue.target}</strong>: {issue.message}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <div className="dashboard-section-heading">Needs action</div>
      {showLoading ? (
        <Card style={{ padding: '1rem' }}>
          <LoadingCard lines={5} titleWidth="24%" />
        </Card>
      ) : (
        <OrdersTable compactFilter={(order) => order.status === 'PENDING' || order.status === 'PROCESSING'} />
      )}

      <style>{`
        @media (max-width: 1100px) {
          .admin-metric-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 620px) {
          .admin-metric-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function SystemHealthPanel({ initialIntegrity }) {
  const [integrity, setIntegrity] = useState(() => initialIntegrity || null);
  const [loading, setLoading] = useState(!initialIntegrity);
  const [refreshing, setRefreshing] = useState(false);

  const loadIntegrity = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const res = await apiRequest('/admin/system-integrity', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
      setIntegrity(res.integrity || null);
    } catch (error) {
      if (!silent) {
        toast.error(error.message || 'Failed to load system health.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!initialIntegrity) {
      loadIntegrity().catch(() => {});
    }
  }, [initialIntegrity]);

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>System Health</h2>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-3)', lineHeight: 1.6 }}>
            Review missing rates, wallets, bank settings, and other business configuration issues before they affect users.
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={() => loadIntegrity({ silent: true }).catch(() => {})} loading={refreshing}>
          Refresh checks
        </Button>
      </div>

      {loading ? (
        <Card><LoadingCard lines={5} titleWidth="24%" /></Card>
      ) : integrity ? (
        <>
          <div className="admin-metric-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '10px', marginBottom: '1rem' }}>
            <MetricCard label="Status" value={integrity.ok ? 'Healthy' : 'Attention'} accent={integrity.ok} />
            <MetricCard label="Issues" value={Number(integrity.issueCount || 0)} />
            <MetricCard label="Errors" value={Number(integrity.errorCount || 0)} />
            <MetricCard label="Warnings" value={Number(integrity.warningCount || 0)} />
          </div>
          <Card>
            <div style={{ display: 'grid', gap: '10px' }}>
              {(Array.isArray(integrity.issues) && integrity.issues.length > 0) ? integrity.issues.map((issue) => (
                <div key={`${issue.code}-${issue.target}`} style={{ padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '5px', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px' }}>{issue.target}</div>
                    <Badge status={issue.severity === 'error' ? 'rejected' : issue.severity === 'warning' ? 'pending' : 'verified'}>
                      {capitalize(issue.severity || 'info')}
                    </Badge>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>{issue.message}</div>
                </div>
              )) : (
                <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>No integrity issues found right now.</div>
              )}
            </div>
          </Card>
        </>
      ) : (
        <Card><div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>System health could not be loaded right now.</div></Card>
      )}
    </div>
  );
}

function formatRateHistoryNumber(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '-';
  return formatNGN(numeric);
}

function RateHistoryPanel() {
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total: 0, limit: 25, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadHistory = async ({ page = 1, append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await apiRequest(`/admin/rate-history?limit=25&page=${page}`, { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
      const nextItems = Array.isArray(res.history) ? res.history : [];
      setHistory((prev) => append ? [...prev, ...nextItems] : nextItems);
      const total = Number(res.total || nextItems.length);
      const limit = Number(res.limit || 25);
      setMeta({
        page: Number(res.page || page),
        total,
        limit,
        hasMore: (Number(res.page || page) * limit) < total,
      });
    } catch (error) {
      toast.error(error.message || 'Failed to load rate history.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadHistory().catch(() => {});
  }, []);

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>Rate History</h2>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-3)', lineHeight: 1.6 }}>
            Track who changed rates, when they changed them, and the previous versus new values saved on PayRocky.
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={() => loadHistory({ page: 1 }).catch(() => {})} loading={loading && history.length > 0}>
          Refresh history
        </Button>
      </div>

      {loading && history.length === 0 ? (
        <Card><LoadingCard lines={6} titleWidth="24%" /></Card>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {history.length === 0 ? (
            <Card><div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>No rate changes recorded yet.</div></Card>
          ) : history.map((entry) => {
            const details = entry.details || {};
            const affectedRates = Array.isArray(details.affected_rates) ? details.affected_rates : [];
            const previous = details.previous || null;
            const next = details.next || null;
            return (
              <Card key={entry.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>
                      {details.apply_globally ? 'BTC benchmark update' : `${String(entry.target_id || '').toUpperCase()} rate update`}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
                      {entry.admin_name || 'Admin'} • {formatDate(entry.created_at)}
                    </div>
                  </div>
                  <Badge status={details.apply_globally ? 'pending' : 'verified'}>
                    {details.apply_globally ? `${affectedRates.length} assets refreshed` : 'Single asset update'}
                  </Badge>
                </div>

                {details.apply_globally ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {affectedRates.slice(0, 12).map((item) => (
                      <div key={item.crypto} style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '12px', color: 'var(--clr-text-2)' }}>
                        <strong style={{ color: 'var(--clr-text)' }}>{String(item.crypto || '').toUpperCase()}</strong>
                        {': '}
                        {formatRateHistoryNumber(item.previous?.buy_rate)} / {formatRateHistoryNumber(item.previous?.sell_rate)}
                        {' -> '}
                        {formatRateHistoryNumber(item.next?.buy_rate)} / {formatRateHistoryNumber(item.next?.sell_rate)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                    <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '6px' }}>Previous</div>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{formatRateHistoryNumber(previous?.buy_rate)} buy</div>
                      <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>{formatRateHistoryNumber(previous?.sell_rate)} sell</div>
                    </div>
                    <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '6px' }}>New</div>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{formatRateHistoryNumber(next?.buy_rate)} buy</div>
                      <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>{formatRateHistoryNumber(next?.sell_rate)} sell</div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
          {meta.hasMore ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="ghost" size="sm" onClick={() => loadHistory({ page: meta.page + 1, append: true }).catch(() => {})} loading={loadingMore}>
                Load more history
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Orders() {
  const { orders, loading, ordersError, ordersMeta, refreshOrders, loadMoreOrders } = useOrders();
  const safeOrders = Array.isArray(orders) ? orders : [];
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    workflowStage: 'ALL',
    disputeStatus: 'ALL',
    flagged: 'ALL',
    asset: 'ALL',
    network: 'ALL',
    fromDate: '',
    toDate: '',
  });
  const assetOptions = useMemo(() => [...new Set(safeOrders.map((order) => String(order.crypto_type || '').toLowerCase()).filter(Boolean))], [safeOrders]);
  const networkOptions = useMemo(() => [...new Set(safeOrders.map((order) => String(order.network || '').toUpperCase()).filter(Boolean))], [safeOrders]);
  const filtered = useMemo(() => {
    const search = normalizeSearchText(filters.search);
    return safeOrders.filter((order) => {
      if (filters.status !== 'ALL' && order.status !== filters.status) return false;
      if (filters.workflowStage !== 'ALL' && order.workflow_stage !== filters.workflowStage) return false;
      if (filters.disputeStatus !== 'ALL' && String(order.dispute_status || 'none') !== filters.disputeStatus) return false;
      if (filters.flagged === 'FLAGGED' && !order.is_flagged) return false;
      if (filters.flagged === 'CLEAR' && order.is_flagged) return false;
      if (filters.asset !== 'ALL' && String(order.crypto_type || '').toLowerCase() !== filters.asset) return false;
      if (filters.network !== 'ALL' && String(order.network || '').toUpperCase() !== filters.network) return false;
      if ((filters.fromDate || filters.toDate) && !isWithinDateRange(order.created_at, filters.fromDate, filters.toDate)) return false;
      if (!search) return true;

      const haystack = [
        order.order_id,
        order.user_name,
        order.crypto_type,
        order.network,
        order.type,
      ].map(normalizeSearchText).join(' ');

      return haystack.includes(search);
    });
  }, [safeOrders, filters]);

  useEffect(() => {
    if (!ordersError || safeOrders.length > 0 || loading) return undefined;
    const retryTimeoutId = window.setTimeout(() => {
      refreshOrders({ page: 1, force: true }).catch(() => {});
    }, 3000);
    return () => window.clearTimeout(retryTimeoutId);
  }, [ordersError, safeOrders.length, loading, refreshOrders]);

  return (
    <div className="fade-up">
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '1.5rem' }}>All Orders</h2>
      <AdminFiltersCard>
        <FilterField label="Search orders">
          <input value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} placeholder="Order ID, user, asset..." style={filterInputStyle} />
        </FilterField>
        <FilterField label="Status">
          <select value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} style={filterInputStyle}>
            {['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'CANCELLED'].map((status) => <option key={status} value={status}>{capitalize(status)}</option>)}
          </select>
        </FilterField>
        <FilterField label="Workflow stage">
          <select value={filters.workflowStage} onChange={(event) => setFilters((prev) => ({ ...prev, workflowStage: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All stages</option>
            {WORKFLOW_STAGE_OPTIONS.map((stage) => <option key={stage} value={stage}>{formatWorkflowStage(stage)}</option>)}
          </select>
        </FilterField>
        <FilterField label="Dispute">
          <select value={filters.disputeStatus} onChange={(event) => setFilters((prev) => ({ ...prev, disputeStatus: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All disputes</option>
            {DISPUTE_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{formatDisputeStatus(status)}</option>)}
          </select>
        </FilterField>
        <FilterField label="Flagged">
          <select value={filters.flagged} onChange={(event) => setFilters((prev) => ({ ...prev, flagged: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All orders</option>
            <option value="FLAGGED">Flagged only</option>
            <option value="CLEAR">Unflagged only</option>
          </select>
        </FilterField>
        <FilterField label="Asset">
          <select value={filters.asset} onChange={(event) => setFilters((prev) => ({ ...prev, asset: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All assets</option>
            {assetOptions.map((asset) => <option key={asset} value={asset}>{asset.toUpperCase()}</option>)}
          </select>
        </FilterField>
        <FilterField label="Network">
          <select value={filters.network} onChange={(event) => setFilters((prev) => ({ ...prev, network: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All networks</option>
            {networkOptions.map((network) => <option key={network} value={network}>{network}</option>)}
          </select>
        </FilterField>
        <FilterField label="From date">
          <input type="date" value={filters.fromDate} onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))} style={filterInputStyle} />
        </FilterField>
        <FilterField label="To date">
          <input type="date" value={filters.toDate} onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))} style={filterInputStyle} />
        </FilterField>
      </AdminFiltersCard>
      {loading && safeOrders.length === 0 ? (
        <Card style={{ padding: '1rem' }}>
          <LoadingCard lines={5} titleWidth="26%" />
        </Card>
      ) : (
        <>
          {ordersError && safeOrders.length === 0 ? (
            <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '10px' }}>{ordersError}</div>
              <Button variant="primary" size="sm" onClick={() => refreshOrders({ page: 1, force: true }).catch(() => {})}>Retry loading orders</Button>
            </Card>
          ) : null}
          <OrdersTable orders={filtered} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '1rem' }}>
            <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
              Showing {filtered.length} visible from {safeOrders.length} loaded orders
            </div>
            {ordersMeta.hasMore ? (
              <Button variant="ghost" size="sm" onClick={() => loadMoreOrders().catch(() => {})} loading={loading}>
                Load more orders
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

function OrdersTable({ orders: passedOrders, compactFilter }) {
  const { orders } = useOrders();
  const safeOrders = Array.isArray(orders) ? orders : [];
  const safePassedOrders = Array.isArray(passedOrders) ? passedOrders : null;
  const orderList = safePassedOrders || safeOrders.filter((order) => (compactFilter ? compactFilter(order) : true));

  return (
    <>
      <Card className="admin-orders-desktop" style={{ padding: 0, overflow: 'hidden' }}>
        <AdminOrderHeader />
        {orderList.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--clr-text-3)', fontSize: '13px' }}>No orders</div>
        ) : (
          orderList.map((order) => <AdminOrderRow key={order.id} order={order} />)
        )}
      </Card>

      <div className="admin-orders-mobile" style={{ display: 'none', gap: '10px' }}>
        {orderList.length === 0 ? (
          <Card><div style={{ textAlign: 'center', color: 'var(--clr-text-3)', fontSize: '13px' }}>No orders</div></Card>
        ) : (
          orderList.map((order) => <AdminOrderCard key={order.id} order={order} />)
        )}
      </div>

      <style>{`
        @media (max-width: 920px) {
          .admin-orders-desktop {
            display: none !important;
          }

          .admin-orders-mobile {
            display: grid !important;
          }
        }
      `}</style>
    </>
  );
}

function UsersPanel() {
  const { users, usersLoading, usersError, usersMeta, refreshUsers, loadMoreUsers, reviewUserKyc, adminSetTemporaryPassword } = useAuth();
  const safeUsers = Array.isArray(users) ? users : [];
  const [filters, setFilters] = useState({
    search: '',
    kycStatus: 'ALL',
    fromDate: '',
    toDate: '',
  });
  const [tempPasswords, setTempPasswords] = useState({});
  const [tempPasswordLoadingFor, setTempPasswordLoadingFor] = useState('');
  const [initialUsersRetryCount, setInitialUsersRetryCount] = useState(0);

  useEffect(() => {
    if (safeUsers.length > 0 || usersLoading || usersError) return;
    refreshUsers().catch(() => {});
  }, [refreshUsers, safeUsers.length, usersLoading, usersError]);

  useEffect(() => {
    if (!usersError || safeUsers.length > 0 || usersLoading || initialUsersRetryCount >= 2) return undefined;
    const retryTimeoutId = window.setTimeout(() => {
      setInitialUsersRetryCount((count) => count + 1);
      refreshUsers().catch(() => {});
    }, 3000);
    return () => window.clearTimeout(retryTimeoutId);
  }, [usersError, safeUsers.length, usersLoading, initialUsersRetryCount, refreshUsers]);

  useEffect(() => {
    if (safeUsers.length > 0) {
      setInitialUsersRetryCount(0);
    }
  }, [safeUsers.length]);

  const pendingFirst = useMemo(() => {
    const search = normalizeSearchText(filters.search);
    return [...safeUsers]
      .filter((user) => {
        if (filters.kycStatus !== 'ALL' && user.kyc_status !== filters.kycStatus) return false;
        if ((filters.fromDate || filters.toDate) && !isWithinDateRange(user.created_at || user.kyc_submitted_at, filters.fromDate, filters.toDate)) return false;
        if (!search) return true;

        const haystack = [
          user.name,
          user.email,
          user.phone,
          user.kyc_document_type,
        ].map(normalizeSearchText).join(' ');

        return haystack.includes(search);
      })
      .sort((a, b) => (a.kyc_status === 'pending' ? -1 : 0) - (b.kyc_status === 'pending' ? -1 : 0));
  }, [safeUsers, filters]);

  const handleReview = async (userId, status) => {
    const res = await reviewUserKyc(userId, status);
    if (res.success) {
      toast.success(`KYC marked as ${status}`);
    } else {
      toast.error(res.error || 'Failed to update user verification');
    }
  };

  const handleTemporaryPasswordChange = (userId, value) => {
    setTempPasswords((prev) => ({ ...prev, [userId]: value }));
  };

  const handleSetTemporaryPassword = async (userId) => {
    const temporaryPassword = String(tempPasswords[userId] || '');
    if (!temporaryPassword) {
      toast.error('Enter a temporary password first.');
      return;
    }

    setTempPasswordLoadingFor(userId);
    const res = await adminSetTemporaryPassword({ userId, temporaryPassword });
    setTempPasswordLoadingFor('');

    if (res.success) {
      toast.success(res.message || 'Temporary password set successfully.');
      setTempPasswords((prev) => ({ ...prev, [userId]: '' }));
    } else {
      toast.error(res.error || 'Failed to set temporary password.');
    }
  };

  return (
    <div className="fade-up">
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '1.5rem' }}>User Management</h2>
      <AdminFiltersCard>
        <FilterField label="Search users">
          <input value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} placeholder="Name, email, phone..." style={filterInputStyle} />
        </FilterField>
        <FilterField label="KYC status">
          <select value={filters.kycStatus} onChange={(event) => setFilters((prev) => ({ ...prev, kycStatus: event.target.value }))} style={filterInputStyle}>
            <option value="ALL">All KYC statuses</option>
            {['unverified', 'pending', 'verified', 'rejected', 'suspended'].map((status) => (
              <option key={status} value={status}>{capitalize(status)}</option>
            ))}
          </select>
        </FilterField>
        <FilterField label="From date">
          <input type="date" value={filters.fromDate} onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))} style={filterInputStyle} />
        </FilterField>
        <FilterField label="To date">
          <input type="date" value={filters.toDate} onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))} style={filterInputStyle} />
        </FilterField>
      </AdminFiltersCard>
      <div style={{ display: 'grid', gap: '12px' }}>
        {usersLoading && safeUsers.length === 0 ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Card key={`user-panel-skeleton-${index}`}>
              <LoadingCard lines={4} titleWidth="22%" />
            </Card>
          ))
        ) : (
          <>
          {usersError && safeUsers.length === 0 ? (
            <Card style={{ padding: '1rem' }}>
              <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '10px' }}>{usersError}</div>
              <Button variant="primary" size="sm" onClick={() => refreshUsers().catch(() => {})}>Retry loading users</Button>
            </Card>
          ) : null}
          {pendingFirst.map((user) => (
          <Card key={user.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '4px' }}>{user.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>{user.email}</div>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{user.phone || '-'}</div>
              </div>
              <Badge status={user.kyc_status}>{capitalize(user.kyc_status)}</Badge>
            </div>

            <div className="admin-user-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <InfoBlock label="Document type" value={user.kyc_document_type || '-'} />
              <InfoBlock label="Document number" value={maskDocumentNumber(user.kyc_document_number)} />
              <InfoBlock label="Uploaded file" value={user.kyc_document_file_name || '-'} />
              <InfoBlock label="Submitted" value={user.kyc_submitted_at ? formatDate(user.kyc_submitted_at) : '-'} />
            </div>

            {user.kyc_document_url && (
              <div style={{ marginBottom: '14px' }}>
                <a
                  href={resolveUploadedAssetUrl(user.kyc_document_url)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '13px', fontWeight: 700, color: 'var(--clr-accent)' }}
                >
                  View uploaded document
                </a>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {user.kyc_status !== 'verified' && <Button variant="success" size="sm" onClick={() => handleReview(user.id, 'verified')}><CheckCircle size={13} /> Approve KYC</Button>}
              {user.kyc_status !== 'rejected' && <Button variant="danger" size="sm" onClick={() => handleReview(user.id, 'rejected')}><XCircle size={13} /> Reject KYC</Button>}
              {user.kyc_status !== 'suspended' && <Button variant="outline" size="sm" onClick={() => handleReview(user.id, 'suspended')}>Suspend</Button>}
            </div>

            <Divider style={{ margin: '14px 0' }} />

            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '6px' }}>Manual recovery password</div>
              <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', lineHeight: 1.7, marginBottom: '10px' }}>
                Set a temporary password for this user. Share it securely with them, then instruct them to change it immediately from their profile page after login.
              </div>
              <div className="admin-temp-password-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '8px', alignItems: 'center' }}>
                <input
                  type="password"
                  value={tempPasswords[user.id] || ''}
                  onChange={(event) => handleTemporaryPasswordChange(user.id, event.target.value)}
                  placeholder="Enter temporary password"
                  style={filterInputStyle}
                />
                <Button
                  variant="primary"
                  size="sm"
                  loading={tempPasswordLoadingFor === user.id}
                  onClick={() => handleSetTemporaryPassword(user.id)}
                >
                  Set temp password
                </Button>
              </div>
            </div>
          </Card>
          ))}
          </>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '1rem' }}>
        <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
          Showing {pendingFirst.length} visible from {safeUsers.length} loaded users
        </div>
        {usersMeta.hasMore ? (
          <Button variant="ghost" size="sm" onClick={() => loadMoreUsers().catch(() => {})} loading={usersLoading}>
            Load more users
          </Button>
        ) : null}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .admin-user-grid {
            grid-template-columns: 1fr !important;
          }

          .admin-temp-password-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

const filterInputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--clr-surface-2)',
  border: '1px solid var(--clr-border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--clr-text)',
  fontSize: '13px',
};

function AdminFiltersCard({ children }) {
  return (
    <Card style={{ marginBottom: '1.25rem' }}>
      <div className="admin-filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
        {children}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .admin-filters-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </Card>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '6px', fontWeight: 700 }}>{label}</div>
      {children}
    </div>
  );
}

function RatesPanel() {
  const { rates, ratesError, fetching, adminUpdateRate, adminRefreshRates } = useRates();
  const [savingRateId, setSavingRateId] = useState('');
  const [dirtyRateIds, setDirtyRateIds] = useState({});
  const [editRates, setEditRates] = useState(() => {
    const initial = {};
    CRYPTO_LIST.forEach((crypto) => {
      const usdPrice = rates[crypto.id]?.usdPrice || 0;
      initial[crypto.id] = {
        buy: toEditableRateValue(getAdminRateInputValue(crypto.id, rates[crypto.id]?.buy || 0, usdPrice)),
        sell: toEditableRateValue(getAdminRateInputValue(crypto.id, rates[crypto.id]?.sell || 0, usdPrice)),
        networkFees: toEditableNetworkFees(rates[crypto.id]?.networkFees || {}, crypto.networks),
      };
    });
    return initial;
  });

  useEffect(() => {
    setEditRates((prev) => {
      const next = { ...prev };
      CRYPTO_LIST.forEach((crypto) => {
        if (dirtyRateIds[crypto.id]) return;
        const usdPrice = rates[crypto.id]?.usdPrice || 0;
        next[crypto.id] = {
          buy: toEditableRateValue(getAdminRateInputValue(crypto.id, rates[crypto.id]?.buy || 0, usdPrice)),
          sell: toEditableRateValue(getAdminRateInputValue(crypto.id, rates[crypto.id]?.sell || 0, usdPrice)),
          networkFees: toEditableNetworkFees(rates[crypto.id]?.networkFees || {}, crypto.networks),
        };
      });
      return next;
    });
  }, [rates, dirtyRateIds]);

  const handleRateValueChange = (cryptoId, field, value) => {
    setDirtyRateIds((prev) => ({ ...prev, [cryptoId]: true }));
    setEditRates((prev) => ({
      ...prev,
      [cryptoId]: {
        ...prev[cryptoId],
        [field]: value,
      },
    }));
  };

  const handleResetRateDraft = (cryptoId) => {
    const usdPrice = rates[cryptoId]?.usdPrice || 0;
    const crypto = CRYPTO_LIST.find((item) => item.id === cryptoId);
    setEditRates((prev) => ({
      ...prev,
      [cryptoId]: {
        buy: toEditableRateValue(getAdminRateInputValue(cryptoId, rates[cryptoId]?.buy || 0, usdPrice)),
        sell: toEditableRateValue(getAdminRateInputValue(cryptoId, rates[cryptoId]?.sell || 0, usdPrice)),
        networkFees: toEditableNetworkFees(rates[cryptoId]?.networkFees || {}, crypto?.networks || []),
      },
    }));
    setDirtyRateIds((prev) => {
      const next = { ...prev };
      delete next[cryptoId];
      return next;
    });
  };

  const handleSave = async (cryptoId) => {
    const crypto = CRYPTO_LIST.find((item) => item.id === cryptoId);
    const rate = editRates[cryptoId];
    const usdPrice = rates[cryptoId]?.usdPrice || 0;
    const isBenchmarkRate = cryptoId === 'btc';
    if (!crypto) return;
    if (!rate.buy || !rate.sell) {
      toast.error('Enter both buy and sell rates');
      return;
    }
    if (!STABLE_RATE_INPUT_IDS.has(cryptoId) && !usdPrice) {
      toast.error('USD market price is missing for this asset');
      return;
    }
    const hasInvalidFee = crypto.networks.some((network) => Number(rate.networkFees?.[network] || 0) < 0);
    if (hasInvalidFee) {
      toast.error('Network fee cannot be negative');
      return;
    }
    try {
      setSavingRateId(cryptoId);
      const networkFees = crypto.networks.reduce((acc, network) => {
        const feeValue = parseFloat(rate.networkFees?.[network]) || 0;
        acc[network] = Math.max(0, feeValue);
        return acc;
      }, {});

      const response = await adminUpdateRate(crypto.id, {
        buy: getStoredRateFromAdminInput(crypto.id, rate.buy, usdPrice),
        sell: getStoredRateFromAdminInput(crypto.id, rate.sell, usdPrice),
        networkFees,
        applyGlobally: isBenchmarkRate,
      });
      setDirtyRateIds((prev) => {
        const next = { ...prev };
        delete next[cryptoId];
        if (response?.appliedGlobally) {
          Object.keys(next).forEach((id) => {
            if (id !== cryptoId) delete next[id];
          });
        }
        return next;
      });
      setEditRates((prev) => {
        const updatedRate = response?.rates?.[cryptoId] || rates[cryptoId];
        const updatedUsdPrice = updatedRate?.usdPrice || usdPrice;
        return {
          ...prev,
          [cryptoId]: {
            buy: toEditableRateValue(getAdminRateInputValue(cryptoId, updatedRate?.buy || 0, updatedUsdPrice)),
            sell: toEditableRateValue(getAdminRateInputValue(cryptoId, updatedRate?.sell || 0, updatedUsdPrice)),
            networkFees: toEditableNetworkFees(updatedRate?.networkFees || {}, crypto.networks),
          },
        };
      });
      toast.success(
        response?.appliedGlobally
          ? 'BTC benchmark updated. All currency rates refreshed.'
          : `${crypto.symbol} rate and fee updated.`
      );
    } catch (error) {
      console.error('[Admin Rate Update Error]', error);
      toast.error(error.message || 'Failed to update rates');
    } finally {
      setSavingRateId('');
    }
  };

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800 }}>Rate Management</h2>
      </div>

      {ratesError ? (
        <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '10px' }}>
            Rate data could not refresh just now. You may be seeing older saved values until the next successful sync.
          </div>
          <Button variant="primary" size="sm" onClick={() => adminRefreshRates().catch(() => {})} loading={fetching}>
            Retry rate refresh
          </Button>
        </Card>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {CRYPTO_LIST.map((crypto) => (
          <Card key={crypto.id}>
            {(() => {
              const usdPrice = rates[crypto.id]?.usdPrice || 0;
              const displayBuyRate = getStoredRateFromAdminInput(crypto.id, editRates[crypto.id]?.buy, usdPrice);
              const displaySellRate = getStoredRateFromAdminInput(crypto.id, editRates[crypto.id]?.sell, usdPrice);
              const adminInputLabel = 'NGN per $1';
              const isBenchmarkRate = crypto.id === 'btc';

              return (
                <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <CryptoLogo cryptoId={crypto.id} size={36} />
              <div>
                <div style={{ fontWeight: 600 }}>{crypto.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>Market: ${rates[crypto.id]?.usdPrice?.toLocaleString() || '-'} USD</div>
              </div>
            </div>
            <div style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: 'var(--clr-text)', marginBottom: '4px' }}>
                {isBenchmarkRate ? `Admin input mode: ${adminInputLabel}` : 'Rate source: BTC benchmark'}
              </div>
              <div>
                {isBenchmarkRate
                  ? <>Enter values like <strong style={{ color: 'var(--clr-text)' }}>1400/$</strong>. PayRocky will save BTC and use that same NGN-per-dollar benchmark to refresh the other coin rates, while keeping their own network fees untouched.</>
                  : <>Enter the rate in NGN per $1 for {crypto.symbol}. Saving here updates only {crypto.symbol} and its own network fees. Users currently see {formatNGN(displayBuyRate || 0)} buy and {formatNGN(displaySellRate || 0)} sell for {crypto.symbol}.</>}
              </div>
            </div>
            <div className="admin-rate-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
              <RateField label={`Buy dashboard rate (${adminInputLabel})`} value={editRates[crypto.id]?.buy} onChange={(value) => handleRateValueChange(crypto.id, 'buy', value)} />
              <RateField label={`Sell dashboard rate (${adminInputLabel})`} value={editRates[crypto.id]?.sell} onChange={(value) => handleRateValueChange(crypto.id, 'sell', value)} />
            </div>
            <div className="admin-network-fee-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
              {crypto.networks.map((network) => (
                <RateField
                  key={network}
                  label={`${network} fee (USD)`}
                  value={editRates[crypto.id]?.networkFees?.[network] ?? 0}
                  onChange={(value) => { setDirtyRateIds((prev) => ({ ...prev, [crypto.id]: true })); setEditRates((prev) => ({
                    ...prev,
                    [crypto.id]: {
                      ...prev[crypto.id],
                      networkFees: {
                        ...prev[crypto.id].networkFees,
                        [network]: value,
                      },
                    },
                  })); }}
                />
              ))}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="primary" size="sm" onClick={() => handleSave(crypto.id)} loading={savingRateId === crypto.id} disabled={Boolean(savingRateId && savingRateId !== crypto.id)}>{isBenchmarkRate ? 'Save BTC benchmark' : 'Save rate & fee'}</Button>
                {dirtyRateIds[crypto.id] ? (
                  <Button variant="ghost" size="sm" onClick={() => handleResetRateDraft(crypto.id)} disabled={Boolean(savingRateId)}>
                    Reset draft
                  </Button>
                ) : null}
              </div>
            </div>
                </>
              );
            })()}
          </Card>
        ))}
      </div>

      <div style={{ marginTop: '1rem', fontSize: '12px', color: 'var(--clr-text-3)', lineHeight: 1.6 }}>All admin inputs here are NGN per $1. Saving BTC refreshes the other coin rates from that benchmark without changing their fees. Saving any other asset updates only that asset and its own fees. Users will see the calculated live NGN value for each coin after refresh.</div>

      <style>{`
        @media (max-width: 760px) {
          .admin-rate-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function WalletsPanel() {
  const { wallets, walletsLoading, walletsError, adminUpdateWallet, refreshWallets } = useWallets();
  const [savingWalletKey, setSavingWalletKey] = useState('');
  const [dirtyWalletKeys, setDirtyWalletKeys] = useState({});
  const [drafts, setDrafts] = useState(() => {
    const initial = {};
    CRYPTO_LIST.forEach((crypto) => {
      initial[crypto.id] = {};
      crypto.networks.forEach((network) => {
        initial[crypto.id][network] = normalizeWalletDraft(wallets?.[crypto.id]?.[network]);
      });
    });
    return initial;
  });

  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      CRYPTO_LIST.forEach((crypto) => {
        const currentDrafts = { ...(prev[crypto.id] || {}) };
        const syncedDrafts = { ...currentDrafts };
        crypto.networks.forEach((network) => {
          const key = `${crypto.id}:${network}`;
          if (dirtyWalletKeys[key]) return;
          syncedDrafts[network] = normalizeWalletDraft(wallets?.[crypto.id]?.[network]);
        });
        next[crypto.id] = syncedDrafts;
      });
      return next;
    });
  }, [wallets, dirtyWalletKeys]);

  useEffect(() => {
    if (!walletsError) return undefined;
    const retryTimeoutId = window.setTimeout(() => {
      refreshWallets(true).catch(() => {});
    }, 3000);
    return () => window.clearTimeout(retryTimeoutId);
  }, [walletsError, refreshWallets]);

  const handleDraftChange = (cryptoId, network, field, value) => {
    const key = `${cryptoId}:${network}`;
    setDirtyWalletKeys((prev) => ({ ...prev, [key]: true }));
    setDrafts((prev) => ({
      ...prev,
      [cryptoId]: {
        ...prev[cryptoId],
        [network]: {
          ...normalizeWalletDraft(prev?.[cryptoId]?.[network]),
          [field]: value,
        },
      },
    }));
  };

  const handleResetWalletDraft = (cryptoId, network) => {
    const key = `${cryptoId}:${network}`;
    setDrafts((prev) => ({
      ...prev,
      [cryptoId]: {
        ...prev[cryptoId],
        [network]: normalizeWalletDraft(wallets?.[cryptoId]?.[network]),
      },
    }));
    setDirtyWalletKeys((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async (cryptoId, network) => {
    const draft = normalizeWalletDraft(drafts?.[cryptoId]?.[network]);
    const address = draft.address.trim();
    if (draft.isEnabled && !address) {
      toast.error(`Enter a wallet address for ${network}`);
      return;
    }
    if (draft.isEnabled && address.length < 20) {
      toast.error(`Wallet address for ${network} seems too short`);
      return;
    }
    if (!draft.isEnabled && !draft.disabledReason.trim()) {
      toast.error(`Add a reason before disabling ${network}`);
      return;
    }
    try {
      setSavingWalletKey(`${cryptoId}:${network}`);
      await adminUpdateWallet(cryptoId, network, {
        address,
        isEnabled: draft.isEnabled,
        disabledReason: draft.disabledReason.trim(),
      });
      setDirtyWalletKeys((prev) => {
        const next = { ...prev };
        delete next[`${cryptoId}:${network}`];
        return next;
      });
      toast.success(`${cryptoId.toUpperCase()} ${network} wallet saved successfully`);
    } catch (error) {
      console.error(`[Wallet Update Error] ${cryptoId} ${network}:`, error);
      toast.error(error.message || 'Failed to update wallet');
    } finally {
      setSavingWalletKey('');
    }
  };

  return (
    <div className="fade-up">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>Wallet Management</h2>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-3)' }}>Replace deposit wallets anytime. Each crypto network can carry its own separate wallet address.</div>
      </div>

      {walletsError ? (
        <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', marginBottom: '10px' }}>
            Wallet data could not refresh just now. You may be seeing older saved values until the next successful sync.
          </div>
          <Button variant="primary" size="sm" onClick={() => refreshWallets(true).catch(() => {})} loading={walletsLoading}>
            Retry wallet refresh
          </Button>
        </Card>
      ) : null}

      <div style={{ display: 'grid', gap: '12px' }}>
        {CRYPTO_LIST.map((crypto) => (
          <Card key={crypto.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <CryptoLogo cryptoId={crypto.id} size={36} />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--clr-text)' }}>{crypto.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Configure a wallet for every supported network.</div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              {crypto.networks.map((network) => (
                <div key={network} style={{ padding: '12px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
                  {(() => {
                    const walletConfig = normalizeWalletDraft(wallets?.[crypto.id]?.[network]);
                    const draftConfig = normalizeWalletDraft(drafts?.[crypto.id]?.[network]);
                    return (
                      <>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--clr-text)', marginBottom: '8px' }}>{network} wallet</div>
                  <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '8px' }}>
                    Current: {walletConfig.address || 'Not configured yet'}
                    {walletConfig.isEnabled ? ' - Enabled' : ` - Disabled${walletConfig.disabledReason ? ` (${walletConfig.disabledReason})` : ''}`}
                  </div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--clr-text)' }}>
                      <input
                        type="checkbox"
                        checked={draftConfig.isEnabled}
                        onChange={(event) => handleDraftChange(crypto.id, network, 'isEnabled', event.target.checked)}
                      />
                      Enable this network for user orders
                    </label>
                    <div className="admin-wallet-row" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'end' }}>
                    <WalletField
                      label={`New ${network} wallet address`}
                      value={draftConfig.address}
                      onChange={(event) => handleDraftChange(crypto.id, network, 'address', event.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button variant="primary" size="sm" onClick={() => handleSave(crypto.id, network)} loading={savingWalletKey === `${crypto.id}:${network}`} disabled={Boolean(savingWalletKey && savingWalletKey !== `${crypto.id}:${network}`)}>Save wallet</Button>
                      {dirtyWalletKeys[`${crypto.id}:${network}`] ? (
                        <Button variant="ghost" size="sm" onClick={() => handleResetWalletDraft(crypto.id, network)} disabled={Boolean(savingWalletKey)}>
                          Reset draft
                        </Button>
                        ) : null}
                    </div>
                    </div>
                    {!draftConfig.isEnabled ? (
                      <WalletField
                        label="Why is this network disabled?"
                        value={draftConfig.disabledReason}
                        onChange={(event) => handleDraftChange(crypto.id, network, 'disabledReason', event.target.value)}
                        placeholder="Example: treasury refill in progress, maintenance, temporary pause"
                      />
                    ) : null}
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <style>{`
        @media (max-width: 760px) {
          .admin-wallet-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function TreasuryPanel({ initialTreasurySummary }) {
  const [treasury, setTreasury] = useState(() => normalizeTreasuryState({}));
  const [summary, setSummary] = useState(() => initialTreasurySummary || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadTreasury = async () => {
    setLoading(true);
    try {
      const res = await apiRequest('/admin/treasury', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
      setTreasury(normalizeTreasuryState(res.treasury || {}));
      setSummary(res.summary || null);
    } catch (error) {
      toast.error(error.message || 'Failed to load treasury.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTreasury().catch(() => {});
  }, []);

  const updateLiquidity = (cryptoId, network, value) => {
    setTreasury((prev) => ({
      ...prev,
      asset_liquidity: {
        ...prev.asset_liquidity,
        [cryptoId]: {
          ...(prev.asset_liquidity?.[cryptoId] || {}),
          [network]: value.replace(/[^0-9.]/g, ''),
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        naira_available: Math.max(0, Number(treasury.naira_available || 0)),
        asset_liquidity: Object.entries(treasury.asset_liquidity || {}).reduce((acc, [cryptoId, networks]) => {
          acc[cryptoId] = Object.entries(networks || {}).reduce((networkAcc, [network, amount]) => {
            networkAcc[network] = Math.max(0, Number(amount || 0));
            return networkAcc;
          }, {});
          return acc;
        }, {}),
      };
      const res = await apiRequest('/admin/update-treasury', {
        method: 'POST',
        timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS,
        body: payload,
      });
      setTreasury(normalizeTreasuryState(res.treasury || {}));
      setSummary(res.summary || null);
      toast.success('Treasury updated.');
    } catch (error) {
      toast.error(error.message || 'Failed to update treasury.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card style={{ padding: '1rem' }}>
        <LoadingCard lines={5} titleWidth="26%" />
      </Card>
    );
  }

  return (
    <div className="fade-up">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>Treasury & Liquidity</h2>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-3)' }}>
          Track available naira, set chain liquidity, and watch pending settlement pressure before accepting more OTC flow.
        </div>
      </div>

      <div className="dashboard-metric-grid" style={{ marginBottom: '1rem' }}>
        <MetricCard label="Available naira" value={formatNGN(summary?.naira_available || 0)} />
        <MetricCard label="Pending sell payouts" value={formatNGN(summary?.pending_sell_naira_outflow || 0)} />
        <MetricCard label="Pending buy obligations" value={`$${Number(summary?.pending_buy_usd_obligation || 0).toFixed(2)}`} />
        <MetricCard label="Liquidity alerts" value={Array.isArray(summary?.issues) ? summary.issues.length : 0} />
      </div>

      {Array.isArray(summary?.issues) && summary.issues.length > 0 ? (
        <Card style={{ marginBottom: '1rem', border: '1px solid rgba(255, 171, 0, 0.28)', background: 'rgba(255, 171, 0, 0.08)' }}>
          <div style={{ fontWeight: 700, marginBottom: '8px' }}>Treasury needs attention</div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {summary.issues.map((issue, index) => (
              <div key={`${issue.code}-${index}`} style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border)', fontSize: '12px', color: 'var(--clr-text-2)' }}>
                {issue.message}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card>
        <div style={{ display: 'grid', gap: '14px' }}>
          <RateField
            label="Available naira liquidity"
            value={treasury.naira_available}
            onChange={(value) => setTreasury((prev) => ({ ...prev, naira_available: value }))}
          />

          <div style={{ display: 'grid', gap: '12px' }}>
            {CRYPTO_LIST.map((crypto) => (
              <div key={`treasury-${crypto.id}`} style={{ padding: '12px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <CryptoLogo cryptoId={crypto.id} size={28} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--clr-text)' }}>{crypto.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>Available crypto liquidity per network</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                  {crypto.networks.map((network) => (
                    <RateField
                      key={`treasury-${crypto.id}-${network}`}
                      label={`${network} liquidity`}
                      value={treasury.asset_liquidity?.[crypto.id]?.[network] || ''}
                      onChange={(value) => updateLiquidity(crypto.id, network, value)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>Save treasury</Button>
        </div>
      </Card>
    </div>
  );
}

function NotificationsPanel({
  notifications,
  setNotifications,
  unreadCount,
  setUnreadCount,
  notificationSettings,
  setNotificationSettings,
}) {
  const [recipientInput, setRecipientInput] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [busyNotificationId, setBusyNotificationId] = useState('');
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [sendingTestPush, setSendingTestPush] = useState(false);
  const [pushConfig, setPushConfig] = useState({ supported: false, permission: 'default', configured: false, enabledOnThisDevice: false });

  useEffect(() => {
    let cancelled = false;

    async function hydratePushState() {
      try {
        const settingsRes = await apiRequest('/admin/notification-settings', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
        const support = await getPushSupportState();
        const registration = support.supported ? await navigator.serviceWorker.getRegistration('/sw.js') : null;
        const subscription = await registration?.pushManager.getSubscription();
        if (!cancelled) {
          setPushConfig((prev) => ({
            ...prev,
            supported: support.supported,
            permission: support.permission,
            configured: Boolean(settingsRes.pushConfigured),
            enabledOnThisDevice: Boolean(subscription),
          }));
        }
      } catch (_) {}
    }

    hydratePushState();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    const idempotencyPayload = notificationSettings;
    const idempotencyKey = getIdempotencyKey('admin:notification-settings', idempotencyPayload);
    try {
      const res = await apiRequest('/admin/notification-settings', {
        method: 'POST',
        body: notificationSettings,
        timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS,
        idempotencyKey,
      });
      setNotificationSettings(normalizeNotificationSettings(res.settings));
      setPushConfig((prev) => ({ ...prev, configured: Boolean(res.pushConfigured) }));
      releaseIdempotencyKey('admin:notification-settings', idempotencyPayload);
      toast.success('Notification settings updated.');
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:notification-settings', idempotencyPayload);
      }
      toast.error(error.message || 'Failed to update notification settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEnablePush = async () => {
    try {
      const res = await enableAdminPushNotifications();
      setNotificationSettings(normalizeNotificationSettings(res.settings));
      const support = await getPushSupportState();
      setPushConfig((prev) => ({
        ...prev,
        supported: support.supported,
        permission: support.permission,
        enabledOnThisDevice: true,
      }));
      toast.success(res.message || 'Push notifications enabled on this device.');
    } catch (error) {
      toast.error(error.message || 'We could not enable push notifications on this device.');
    }
  };

  const handleDisablePush = async () => {
    try {
      const res = await disableAdminPushNotifications();
      const settingsRes = await apiRequest('/admin/notification-settings', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
      setNotificationSettings(normalizeNotificationSettings(settingsRes.settings));
      setPushConfig((prev) => ({
        ...prev,
        configured: Boolean(settingsRes.pushConfigured),
        enabledOnThisDevice: false,
      }));
      toast.success(res.message || 'Push notifications disabled on this device.');
    } catch (error) {
      toast.error(error.message || 'We could not disable push notifications on this device.');
    }
  };

  const handleSendTestPush = async () => {
    setSendingTestPush(true);
    try {
      const res = await apiRequest('/admin/push/test', {
        method: 'POST',
        body: {},
        timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS,
      });
      toast.success(res.message || 'Test push notification sent.');
    } catch (error) {
      toast.error(error.message || 'Test push notification failed.');
    } finally {
      setSendingTestPush(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    setBusyNotificationId(notificationId);
    try {
      const res = await apiRequest('/admin/notifications/mark-read', {
        method: 'POST',
        body: { notificationId },
        timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS,
      });
      setNotifications((prev) => prev.map((item) => (item.id === notificationId ? { ...item, is_read: true } : item)));
      setUnreadCount(Number(res.unreadCount || 0));
    } catch (error) {
      toast.error(error.message || 'Failed to update notification.');
    } finally {
      setBusyNotificationId('');
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await apiRequest('/admin/notifications/mark-all-read', {
        method: 'POST',
        body: {},
        timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS,
      });
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read.');
    } catch (error) {
      toast.error(error.message || 'Failed to mark notifications as read.');
    } finally {
      setMarkingAllRead(false);
    }
  };

  return (
    <div className="fade-up" style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>Admin Notifications</h2>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-3)' }}>
            Receive order, proof upload, and KYC alerts in-app and by push notification.
          </div>
        </div>
        <Badge status="PENDING">{unreadCount} unread</Badge>
      </div>

      <Card>
        <div style={{ display: 'grid', gap: '14px' }}>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Delivery channels</div>
          <div className="admin-notification-toggle-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <NotificationToggle
              label="In-app dashboard alerts"
              checked={notificationSettings.in_app_enabled}
              onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, in_app_enabled: checked }))}
            />
            <NotificationToggle
              label="Browser push alerts"
              checked={notificationSettings.push_enabled}
              onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, push_enabled: checked }))}
              icon={<Bell size={14} />}
            />
          </div>

          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Push notifications on this device</div>
            <div style={{ padding: '14px', borderRadius: '16px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', display: 'grid', gap: '10px' }}>
              <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
                {pushConfig.supported
                  ? 'Enable web push so PayRocky can alert this phone or browser even when the admin dashboard is not open.'
                  : 'This browser does not support web push notifications.'}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--clr-text-3)' }}>
                <span>Permission: <strong style={{ color: 'var(--clr-text)' }}>{pushConfig.permission}</strong></span>
                <span>Configured: <strong style={{ color: 'var(--clr-text)' }}>{pushConfig.configured ? 'Yes' : 'No'}</strong></span>
                <span>Subscribed devices: <strong style={{ color: 'var(--clr-text)' }}>{notificationSettings.push_subscription_count || 0}</strong></span>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button variant="primary" size="sm" onClick={handleEnablePush} disabled={!pushConfig.supported || !pushConfig.configured || pushConfig.enabledOnThisDevice}>
                  Enable on this device
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDisablePush} disabled={!pushConfig.enabledOnThisDevice}>
                  Disable on this device
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSendTestPush} loading={sendingTestPush} disabled={!pushConfig.configured || !(notificationSettings.push_subscription_count > 0)}>
                  Send test push
                </Button>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', lineHeight: 1.7 }}>
                On iPhone and iPad, push notifications usually require the admin dashboard to be saved to the home screen first before Apple shows notification-bar alerts.
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Alert events</div>
            <div className="admin-notification-events-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
              <NotificationToggle label="New buy orders" checked={notificationSettings.event_preferences.new_buy_order} onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, event_preferences: { ...prev.event_preferences, new_buy_order: checked } }))} />
              <NotificationToggle label="New sell orders" checked={notificationSettings.event_preferences.new_sell_order} onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, event_preferences: { ...prev.event_preferences, new_sell_order: checked } }))} />
              <NotificationToggle label="Payment proof uploads" checked={notificationSettings.event_preferences.proof_uploaded} onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, event_preferences: { ...prev.event_preferences, proof_uploaded: checked } }))} />
              <NotificationToggle label="KYC submissions" checked={notificationSettings.event_preferences.kyc_submitted} onChange={(checked) => setNotificationSettings((prev) => ({ ...prev, event_preferences: { ...prev.event_preferences, kyc_submitted: checked } }))} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" onClick={handleSaveSettings} loading={savingSettings}>Save notification settings</Button>
          </div>
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--clr-text)' }}>Recent notifications</div>
            <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Most recent admin alerts from orders, proof uploads, and KYC.</div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleMarkAllRead} loading={markingAllRead} disabled={!unreadCount}>Mark all as read</Button>
        </div>

        <div style={{ display: 'grid', gap: '10px' }}>
          {notifications.length ? notifications.map((notification) => (
            <div key={notification.id} style={{ padding: '14px', borderRadius: '16px', background: notification.is_read ? 'var(--clr-surface-2)' : 'rgba(143, 59, 46, 0.06)', border: `1px solid ${notification.is_read ? 'var(--clr-border)' : 'rgba(143, 59, 46, 0.18)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '4px' }}>{notification.title}</div>
                  <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.65 }}>{notification.message}</div>
                </div>
                <Badge status={notification.is_read ? 'PROCESSING' : 'PENDING'}>{notification.is_read ? 'Read' : 'Unread'}</Badge>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
                  {notification.order_id ? `Order ${notification.order_id}` : notification.target_id || notification.type} • {formatDate(notification.created_at)}
                </div>
                {!notification.is_read ? (
                  <Button variant="ghost" size="sm" onClick={() => handleMarkRead(notification.id)} loading={busyNotificationId === notification.id}>
                    Mark as read
                  </Button>
                ) : null}
              </div>
            </div>
          )) : (
            <div style={{ fontSize: '13px', color: 'var(--clr-text-3)', textAlign: 'center', padding: '1rem 0' }}>
              No admin notifications yet.
            </div>
          )}
        </div>
      </Card>

      <style>{`
        @media (max-width: 760px) {
          .admin-notification-toggle-grid,
          .admin-notification-events-grid,
          .admin-recipient-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function HomepageBannerPanel() {
  const [banner, setBanner] = useState(() => normalizeHomepageBanner({}));
  const [draft, setDraft] = useState(() => normalizeHomepageBanner({}));
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewBroken, setPreviewBroken] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadBanner() {
      try {
        const res = await apiRequest('/admin/homepage-banner', { timeoutMs: ADMIN_PANEL_REQUEST_TIMEOUT_MS });
        if (cancelled) return;
        const next = normalizeHomepageBanner(res.banner);
        setBanner(next);
        setDraft(next);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || 'Failed to load homepage banner.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBanner();
    return () => {
      cancelled = true;
    };
  }, []);

  const previewUrl = useMemo(() => {
    if (selectedFile) {
      return URL.createObjectURL(selectedFile);
    }
    const baseUrl = resolveUploadedAssetUrl(draft.image_url || banner.image_url);
    if (!baseUrl) return '';
    const versionToken = encodeURIComponent(draft.updated_at || banner.updated_at || draft.image_url || banner.image_url || '');
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${versionToken}`;
  }, [banner.image_url, banner.updated_at, draft.image_url, draft.updated_at, selectedFile]);

  useEffect(() => {
    setPreviewBroken(false);
  }, [previewUrl]);

  useEffect(() => () => {
    if (selectedFile) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl, selectedFile]);

  const handleSave = async () => {
    if (!draft.whatsapp_number) {
      toast.error('Enter the WhatsApp number for the banner.');
      return;
    }

    if (!draft.whatsapp_message.trim()) {
      toast.error('Enter the message users should send from the banner.');
      return;
    }

    if (!selectedFile && !banner.image_url) {
      toast.error('Upload a landscape banner image first.');
      return;
    }

    const formData = new FormData();
    formData.append('whatsapp_number', draft.whatsapp_number);
    formData.append('whatsapp_message', draft.whatsapp_message.trim());
    if (selectedFile) {
      formData.append('banner', selectedFile);
    }

    setSaving(true);
    const idempotencyPayload = {
      whatsapp_number: draft.whatsapp_number,
      whatsapp_message: draft.whatsapp_message.trim(),
      file_name: selectedFile?.name || '',
      file_size: Number(selectedFile?.size || 0),
      file_type: selectedFile?.type || '',
    };
    const idempotencyKey = getIdempotencyKey('admin:update-homepage-banner', idempotencyPayload);
    try {
      const res = await apiRequest('/admin/update-homepage-banner', {
        method: 'POST',
        body: formData,
        timeoutMs: 12000,
        idempotencyKey,
      });
      const next = normalizeHomepageBanner(res.banner);
      setBanner(next);
      setDraft(next);
      setSelectedFile(null);
      releaseIdempotencyKey('admin:update-homepage-banner', idempotencyPayload);
      toast.success('PayRocky Alert Board updated.');
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:update-homepage-banner', idempotencyPayload);
      }
      toast.error(error.message || 'Failed to update homepage banner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" style={{ maxWidth: '940px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>PayRocky Alert Board</h2>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-3)', lineHeight: 1.6 }}>
          Upload one landscape banner for the homepage. When users tap it, they will open WhatsApp with the message you set here.
        </div>
      </div>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>Recommended banner style</div>
          <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
            Use a clean landscape image, ideally around <strong>1600 x 500</strong> or any similar wide ratio. Upload only <strong>JPG</strong>, <strong>PNG</strong>, or <strong>WEBP</strong> so the homepage can render it reliably across devices.
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '10px' }}>Current live banner preview</div>
        {loading ? (
          <Skeleton width="100%" height="180px" radius="18px" />
        ) : previewUrl && !previewBroken ? (
          <div style={{ overflow: 'hidden', borderRadius: '18px', border: '1px solid var(--clr-border)', background: 'var(--clr-surface-2)' }}>
            <img
              src={previewUrl}
              alt="PayRocky Alert Board preview"
              onError={() => setPreviewBroken(true)}
              style={{ width: '100%', aspectRatio: '16 / 5', maxHeight: '240px', minHeight: '150px', objectFit: 'contain', display: 'block', background: 'var(--clr-surface-2)' }}
            />
          </div>
        ) : (
          <div style={{ padding: '16px', borderRadius: '16px', background: 'var(--clr-surface-2)', border: '1px dashed var(--clr-border-md)', color: 'var(--clr-text-3)', fontSize: '13px' }}>
            {banner.image_url || selectedFile ? 'The uploaded banner could not be previewed. Try a PNG, JPG, or WEBP image again.' : 'No banner uploaded yet.'}
          </div>
        )}
      </Card>

      <Card>
        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '5px' }}>Upload banner image</div>
            <label style={{ display: 'grid', gap: '8px', padding: '14px', borderRadius: '16px', background: 'var(--clr-surface-2)', border: '1px dashed var(--clr-border-md)', cursor: 'pointer' }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)' }}>
                {selectedFile ? selectedFile.name : 'Choose a landscape banner image'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>
                PNG, JPG, or WEBP. Best for a wide banner that fits homepage mobile and desktop view.
              </div>
            </label>
          </div>

          <div className="admin-banner-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <WalletField
              label="WhatsApp number with country code"
              value={draft.whatsapp_number}
              onChange={(event) => setDraft((prev) => ({ ...prev, whatsapp_number: event.target.value.replace(/[^\d+\s()-]/g, '') }))}
              placeholder="+234 801 234 5678"
            />
            <div style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface-2)', alignSelf: 'end' }}>
              <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>Banner location</div>
              <div style={{ fontSize: '13px', color: 'var(--clr-text)' }}>Homepage, above Recent Transactions Live</div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '5px' }}>WhatsApp prompt message</div>
            <textarea
              value={draft.whatsapp_message}
              onChange={(event) => setDraft((prev) => ({ ...prev, whatsapp_message: event.target.value }))}
              rows={4}
              placeholder="Hello PayRocky, I saw the homepage banner and I would like to know more."
              style={{ width: '100%', padding: '12px 14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border-md)', borderRadius: 'var(--radius-md)', color: 'var(--clr-text)', fontSize: '14px', resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>

          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>Save homepage banner</Button>
        </div>
      </Card>

      <style>{`
        @media (max-width: 760px) {
          .admin-banner-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function BankingPanel() {
  const { bankDetails, adminUpdateBankDetails } = useBankSettings();
  const [draft, setDraft] = useState(() => ({ ...bankDetails }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({ ...bankDetails });
  }, [bankDetails]);

  const handleSave = async () => {
    if (!draft.bank_name?.trim() || !draft.account_name?.trim() || !/^\d{10}$/.test(draft.account_number || '')) {
      toast.error('Enter a valid bank name, account name, and 10-digit account number');
      return;
    }
    try {
      setSaving(true);
      await adminUpdateBankDetails({
        bank_name: draft.bank_name.trim(),
        account_name: draft.account_name.trim(),
        account_number: draft.account_number.trim(),
      });
      toast.success('Bank payment details updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update bank details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" style={{ maxWidth: '760px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '6px' }}>Buy Payment Bank Details</h2>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-3)' }}>Users buying crypto will see these account details when making bank transfer payment.</div>
      </div>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '10px' }}>Current live details</div>
        <div className="admin-bank-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          <InfoBlock label="Bank name" value={bankDetails.bank_name || 'Not configured'} />
          <InfoBlock label="Account name" value={bankDetails.account_name || 'Not configured'} />
          <InfoBlock label="Account number" value={bankDetails.account_number || 'Not configured'} />
        </div>
      </Card>

      <Card>
        <div style={{ display: 'grid', gap: '12px' }}>
          <WalletField label="Bank name" value={draft.bank_name || ''} onChange={(event) => setDraft((prev) => ({ ...prev, bank_name: event.target.value }))} />
          <WalletField label="Account name" value={draft.account_name || ''} onChange={(event) => setDraft((prev) => ({ ...prev, account_name: event.target.value }))} />
          <WalletField label="Account number" value={draft.account_number || ''} onChange={(event) => setDraft((prev) => ({ ...prev, account_number: event.target.value.replace(/\D/g, '').slice(0, 10) }))} />
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>Save bank details</Button>
        </div>
      </Card>
    </div>
  );
}

function NotificationToggle({ label, checked, onChange, icon = null }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', cursor: 'pointer' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--clr-text)' }}>
        {icon}
        {label}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

function RateField({ label, value, onChange, disabled = false }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '5px' }}>{label}</div>
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ''}
        disabled={disabled}
        readOnly={disabled}
        onFocus={(event) => {
          if (disabled) return;
          event.target.select();
        }}
        onChange={(event) => {
          if (disabled) return;
          const nextValue = event.target.value.replace(/[^0-9.]/g, '');
          onChange(nextValue);
        }}
        style={{ width: '100%', padding: '9px 12px', background: disabled ? 'var(--clr-surface-3)' : 'var(--clr-surface-2)', border: '1px solid var(--clr-border-md)', borderRadius: 'var(--radius-md)', color: disabled ? 'var(--clr-text-3)' : 'var(--clr-text)', fontSize: '14px', cursor: disabled ? 'not-allowed' : 'text' }}
      />
    </div>
  );
}

function WalletField({ label, value, onChange, placeholder = '' }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '5px' }}>{label}</div>
      <input type="text" value={value} onChange={onChange} placeholder={placeholder} spellCheck="false" autoCapitalize="off" autoCorrect="off" style={{ width: '100%', padding: '9px 12px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border-md)', borderRadius: 'var(--radius-md)', color: 'var(--clr-text)', fontSize: '14px', fontFamily: 'var(--font-mono)' }} />
    </div>
  );
}

function InfoBlock({ label, value, action = null }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>{label}</div>
        {action}
      </div>
      <div style={{ fontSize: '13px', color: 'var(--clr-text)', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function AdminOrderHeader() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 60px 100px 90px 110px 120px minmax(320px, 1fr)', padding: '10px 16px', background: 'var(--clr-surface-2)', borderBottom: '1px solid var(--clr-border)', fontSize: '11px', fontWeight: 600, color: 'var(--clr-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      <span>Order ID</span><span>Type</span><span>User</span><span>Asset</span><span>NGN</span><span>Date / Time</span><span>Details & Actions</span>
    </div>
  );
}

function OrderWorkflowControls({ order, compact = false }) {
  const { adminApprove, adminReject, adminUpdateOrderWorkflow } = useOrders();
  const [busy, setBusy] = useState(false);
  const [workflowStage, setWorkflowStage] = useState(getEditableWorkflowStageValue(order));
  const [disputeStatus, setDisputeStatus] = useState(order.dispute_status || 'none');
  const [flagged, setFlagged] = useState(Boolean(order.is_flagged));
  const [flagReason, setFlagReason] = useState(order.flag_reason || '');
  const [note, setNote] = useState('');
  const [checklist, setChecklist] = useState(() => ({ ...(order.operations_checklist || {}) }));
  const workflowStageOptions = getEditableWorkflowStageOptions(order.type);

  useEffect(() => {
    setWorkflowStage(getEditableWorkflowStageValue(order));
    setDisputeStatus(order.dispute_status || 'none');
    setFlagged(Boolean(order.is_flagged));
    setFlagReason(order.flag_reason || '');
    setChecklist({ ...(order.operations_checklist || {}) });
    setNote('');
  }, [order.id, order.updated_at, order.workflow_stage, order.dispute_status, order.is_flagged, order.flag_reason, order.operations_checklist]);

  const canApprove = order.status === 'PENDING' || order.status === 'PROCESSING';
  const canReject = order.status === 'PENDING' || order.status === 'PROCESSING';

  const handleSave = async () => {
    try {
      setBusy(true);
      const res = await adminUpdateOrderWorkflow(order.order_id, {
        workflow_stage: workflowStage,
        dispute_status: disputeStatus,
        is_flagged: flagged,
        flag_reason: flagged ? flagReason.trim() : '',
        operations_checklist: checklist,
        note: note.trim(),
      });
      if (!res?.success) {
        toast.error(res?.error || 'Failed to update workflow');
        return;
      }
      setNote('');
      toast.success(`Workflow updated for ${order.order_id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update workflow');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    try {
      setBusy(true);
      const res = await adminApprove(order.order_id);
      if (res.success) toast.success(`Order ${order.order_id} approved`);
      else toast.error(res.error || 'Failed to approve order');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    try {
      setBusy(true);
      const res = await adminReject(order.order_id);
      if (res.success) toast.error(`Order ${order.order_id} rejected`);
      else toast.error(res.error || 'Failed to reject order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: compact ? '10px' : '12px', padding: compact ? '10px' : '12px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge status={order.status}>{order.status}</Badge>
        <Badge status={order.workflow_stage || 'processing'}>{formatWorkflowStage(order.workflow_stage)}</Badge>
        {order.is_flagged ? <Badge status="pending">Flagged</Badge> : null}
        {String(order.dispute_status || 'none') !== 'none' ? <Badge status="processing">{formatDisputeStatus(order.dispute_status)}</Badge> : null}
        {order.rate_locked_until ? <span style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>Rate lock: {formatDate(order.rate_locked_until)}</span> : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
        <InfoBlock label="Workflow stage" value={formatWorkflowStage(order.workflow_stage)} />
        <InfoBlock label="Checklist" value={getChecklistProgress(order.operations_checklist)} />
        <InfoBlock label="Dispute" value={formatDisputeStatus(order.dispute_status)} />
        <InfoBlock label="Flag reason" value={order.flag_reason || 'No active flag'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
        <FilterField label="Workflow stage">
          <select value={workflowStage} onChange={(event) => setWorkflowStage(event.target.value)} style={filterInputStyle}>
            {workflowStageOptions.map((stage) => (
              <option key={stage} value={stage}>{formatWorkflowStage(stage)}</option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Dispute status">
          <select value={disputeStatus} onChange={(event) => setDisputeStatus(event.target.value)} style={filterInputStyle}>
            {DISPUTE_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{formatDisputeStatus(status)}</option>
            ))}
          </select>
        </FilterField>
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--clr-text)' }}>
          <input type="checkbox" checked={flagged} onChange={(event) => setFlagged(event.target.checked)} />
          Flag this order for manual attention
        </label>
        {flagged ? (
          <WalletField
            label="Flag reason"
            value={flagReason}
            onChange={(event) => setFlagReason(event.target.value)}
            placeholder="Reason for hold, mismatch, or follow-up"
          />
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700 }}>Operational checklist</div>
        <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          {CHECKLIST_FIELDS.map(({ key, label }) => (
            <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border)', fontSize: '12px', color: 'var(--clr-text)' }}>
              <input type="checkbox" checked={Boolean(checklist?.[key])} onChange={(event) => setChecklist((prev) => ({ ...prev, [key]: event.target.checked }))} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700 }}>Internal notes</div>
        {Array.isArray(order.internal_notes) && order.internal_notes.length > 0 ? (
          <div style={{ display: 'grid', gap: '8px' }}>
            {order.internal_notes.slice(-2).reverse().map((item) => (
              <div key={item.id} style={{ padding: '10px 12px', borderRadius: '12px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>
                  {item.author_name || 'Admin'} - {formatDate(item.created_at)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>{item.body}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>No internal notes yet.</div>
        )}
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder="Leave an internal note for operations, treasury, or support follow-up"
          style={{ width: '100%', padding: '12px 14px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border-md)', borderRadius: 'var(--radius-md)', color: 'var(--clr-text)', fontSize: '13px', resize: 'vertical', lineHeight: 1.6 }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" onClick={handleSave} loading={busy}>Save workflow</Button>
        {canApprove ? <Button variant="success" size="sm" onClick={handleApprove} loading={busy}><CheckCircle size={13} /> Approve</Button> : null}
        {canReject ? <Button variant="danger" size="sm" onClick={handleReject} loading={busy}><XCircle size={13} /> Reject</Button> : null}
      </div>
    </div>
  );
}

function AdminOrderRow({ order }) {
  const orderTypeLabel = String(order.type || '-').toUpperCase();
  const assetLabel = String(order.crypto_type || '-').toUpperCase();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 60px 100px 90px 110px 120px minmax(320px, 1fr)', padding: '12px 16px', borderBottom: '1px solid var(--clr-border)', alignItems: 'start', fontSize: '13px', gap: '10px' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--clr-accent)' }}>{order.order_id}</span>
      <span><Badge status={order.type}>{orderTypeLabel}</Badge></span>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-2)' }}>{order.user_name}</span>
      <span style={{ fontSize: '12px' }}>{assetLabel}</span>
      <span style={{ fontWeight: 600 }}>{formatNGN(order.naira_value)}</span>
      <span style={{ fontSize: '12px', color: 'var(--clr-text-3)', lineHeight: 1.6 }}>{formatDate(order.created_at)}</span>
      <div style={{ display: 'grid', gap: '10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
          <InfoBlock label="Crypto amount" value={`${formatCrypto(Number(order.crypto_amount || 0), order.crypto_type)} ${assetLabel}`} />
          <InfoBlock label="Crypto value (USD)" value={`$${Number(order.usd_value || 0).toFixed(2)}`} />
          {order.type === 'buy' && <InfoBlock label="Fee (USD)" value={`$${Number(order.fee_usd || 0).toFixed(2)}`} />}
          {order.type === 'buy' && <InfoBlock label="Total payable" value={`$${Number(order.total_usd_value || order.usd_value || 0).toFixed(2)}`} />}
          <InfoBlock label="Rate used" value={formatNGN(order.rate_used)} />
          <InfoBlock
            label={order.type === 'buy' ? 'Receiving wallet' : 'Deposit wallet'}
            value={truncateAddress(order.wallet_address, 10, 8)}
            action={(
              <button
                type="button"
                onClick={() => copyToClipboard(order.wallet_address, `${order.type === 'buy' ? 'Receiving' : 'Deposit'} wallet copied.`)}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '10px', background: 'var(--clr-surface)', border: '1px solid var(--clr-border)', color: 'var(--clr-text-2)' }}
                title="Copy wallet"
              >
                <Copy size={13} />
              </button>
            )}
          />
          {order.type === 'buy' && <InfoBlock label="Wallet expires" value={order.wallet_address_expires ? 'Yes' : 'No'} />}
          {order.type === 'buy' && order.wallet_address_expires && <InfoBlock label="Expiry contact" value={order.wallet_expiry_contact_phone || '-'} />}
          {order.type === 'buy' && order.wallet_address_expires && <InfoBlock label="Expiry window" value={order.wallet_expiry_minutes ? `${order.wallet_expiry_minutes} minutes` : '-'} />}
          {order.type === 'sell' && <InfoBlock label="Bank details" value={`${order.bank_name || '-'} - ${order.account_number || '-'} - ${order.account_name || '-'}`} />}
          <InfoBlock label="Updated" value={formatDate(order.updated_at)} />
        </div>
        {order.proof_image ? (
          <a
            href={resolveUploadedAssetUrl(order.proof_image)}
            target="_blank"
            rel="noreferrer"
            style={{ padding: '8px 12px', width: 'fit-content', background: 'var(--clr-surface)', color: 'var(--clr-accent)', border: '1px solid var(--clr-border)', borderRadius: 'var(--radius-sm)', fontSize: '11px', fontWeight: 700 }}
          >
            View Proof
          </a>
        ) : null}
        <OrderWorkflowControls order={order} />
      </div>
    </div>
  );
}

function AdminOrderCard({ order }) {
  const orderTypeLabel = String(order.type || '-').toUpperCase();
  const assetLabel = String(order.crypto_type || '-').toUpperCase();

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>{order.order_id}</div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--clr-text)' }}>{order.user_name}</div>
        </div>
        <Badge status={order.status}>{order.status}</Badge>
      </div>
      <div style={{ display: 'grid', gap: '6px', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Type: {orderTypeLabel}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Asset: {assetLabel}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Crypto amount: {formatCrypto(Number(order.crypto_amount || 0), order.crypto_type)} {assetLabel}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Crypto value: ${Number(order.usd_value || 0).toFixed(2)}</div>
        {order.type === 'buy' && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Fee: ${Number(order.fee_usd || 0).toFixed(2)}</div>}
        {order.type === 'buy' && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Total payable: ${Number(order.total_usd_value || order.usd_value || 0).toFixed(2)}</div>}
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Naira value: {formatNGN(order.naira_value)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Rate used: {formatNGN(order.rate_used)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Workflow: {formatWorkflowStage(order.workflow_stage)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Checklist: {getChecklistProgress(order.operations_checklist)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>{order.type === 'buy' ? 'Receiving wallet' : 'Deposit wallet'}: {truncateAddress(order.wallet_address, 10, 8)}</span>
          <button
            type="button"
            onClick={() => copyToClipboard(order.wallet_address, `${order.type === 'buy' ? 'Receiving' : 'Deposit'} wallet copied.`)}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '5px', borderRadius: '10px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', color: 'var(--clr-text-2)' }}
            title="Copy wallet"
          >
            <Copy size={13} />
          </button>
        </div>
        {order.type === 'buy' && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Wallet expires: {order.wallet_address_expires ? 'Yes' : 'No'}</div>}
        {order.type === 'buy' && order.wallet_address_expires && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Expiry contact: {order.wallet_expiry_contact_phone || '-'}</div>}
        {order.type === 'buy' && order.wallet_address_expires && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Expiry window: {order.wallet_expiry_minutes ? `${order.wallet_expiry_minutes} minutes` : '-'}</div>}
        {order.type === 'sell' && <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Bank details: {order.bank_name || '-'} - {order.account_number || '-'} - {order.account_name || '-'}</div>}
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Date: {formatDate(order.created_at)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Proof: {order.proof_image ? 'Uploaded' : 'None'}</div>
      </div>
      {order.proof_image && (
        <a
          href={resolveUploadedAssetUrl(order.proof_image)}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '13px', fontWeight: 700, color: 'var(--clr-accent)' }}
        >
          View uploaded payment proof
        </a>
      )}
      <OrderWorkflowControls order={order} compact />
    </Card>
  );
}

